import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getTokenFromStorage } from '@/utils/auth';
import {
  getCampaignCollaborations,
  updateCampaignCollaborationNote,
  type ClientCampaignCollaboration,
  type ClientCampaignCollaborationsResponse,
} from '@/api/beatlyApi';
import { filterCampaignCollaborationsInviteAccepted } from '@/utils/clientCampaignCollaborationInviteAccepted';

/** GET collaborations: filtered list for Content Review plus full client-accepted list for Profiles address merge. */
export type CampaignCollaborationsQueryData = ClientCampaignCollaborationsResponse & {
  collaborationsClientAccepted: ClientCampaignCollaboration[];
};

export interface UseCampaignCollaborationsParams {
  campaignId: string | undefined;
  enabled?: boolean;
}

/** Same validity rule as drafts hook — API ids look like `123456-cpg`. */
function isValidCampaignId(id: string | undefined): boolean {
  return Boolean(id && id.length >= 8 && id.includes('-'));
}

/**
 * GET /app/v2/client/campaign/:campaignId/collaborations
 *
 * Do **not** gate on `user.is_client` here: if it were false/undefined in storage (stale shape),
 * this query would never run while `/drafts` still would — Content Review empty, Approval full.
 * The API returns `NotClientException` for non-clients (same as drafts).
 *
 * Returned `collaborations` are filtered to invite-accepted rows (`ONGOING` / `DONE`) only.
 * `collaborationsClientAccepted` is the unfiltered API list (client accepted curation; may include `INVITED`).
 */
export function campaignCollaborationsQueryKey(campaignId: string | undefined) {
  return ['client', 'campaign', 'collaborations', campaignId] as const;
}

export function useCampaignCollaborations({ campaignId, enabled = true }: UseCampaignCollaborationsParams) {
  const token = getTokenFromStorage();
  const shouldRun = Boolean(token && campaignId && isValidCampaignId(campaignId) && enabled);

  return useQuery({
    queryKey: campaignCollaborationsQueryKey(campaignId),
    queryFn: async () => getCampaignCollaborations(campaignId!, token!),
    select: (data: ClientCampaignCollaborationsResponse): CampaignCollaborationsQueryData => {
      const raw = data.collaborations;
      return {
        ...data,
        collaborations: filterCampaignCollaborationsInviteAccepted(raw),
        collaborationsClientAccepted: raw,
      };
    },
    enabled: shouldRun,
    retry: false,
  });
}

/** Copy the team-note fields of `updated` onto the matching row (other rows and fields untouched). */
export function mergeCollaborationNote(
  data: ClientCampaignCollaborationsResponse | undefined,
  updated: Pick<
    ClientCampaignCollaboration,
    'collaboration_id' | 'client_note' | 'client_note_editor_name' | 'date_client_note_updated'
  >,
): ClientCampaignCollaborationsResponse | undefined {
  if (!data) return data;
  return {
    ...data,
    collaborations: data.collaborations.map((c) =>
      c.collaboration_id === updated.collaboration_id
        ? {
            ...c,
            client_note: updated.client_note ?? null,
            client_note_editor_name: updated.client_note_editor_name ?? null,
            date_client_note_updated: updated.date_client_note_updated ?? null,
          }
        : c,
    ),
  };
}

/**
 * PATCH the shared team note of one collaboration. On success the cached collaborations list is patched in place
 * from the API response (no refetch: the list call is heavy and nothing else on the row changes).
 */
export function useUpdateCollaborationNote(campaignId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ collaborationId, note }: { collaborationId: string; note: string }) => {
      const token = getTokenFromStorage();
      if (!token) throw new Error('Not signed in');
      return updateCampaignCollaborationNote(campaignId, collaborationId, note, token);
    },
    onSuccess: (updated) => {
      queryClient.setQueryData<ClientCampaignCollaborationsResponse>(
        campaignCollaborationsQueryKey(campaignId),
        (prev) => mergeCollaborationNote(prev, updated),
      );
    },
  });
}
